import { CALLBACK_PATH } from './settings';

/**
 * What the console needs from Okta (CHG-050), as one small interface so screens and tests never
 * touch `OktaAuth`: `wrapOkta` adapts the real client, `FakeAuth` (src/test) stands in for it.
 */
export interface AuthClient {
  isAuthenticated(): Promise<boolean>;
  /** The access token to send; null when there is none. */
  accessToken(): Promise<string | null>;
  /** A silent renewal of the access token; null when it cannot be had. */
  renewAccessToken(): Promise<string | null>;
  /** Redirect to Okta; `originalUri` is the page to come back to. */
  signIn(originalUri: string): Promise<void>;
  /** Finish the sign-in at the callback and say which page to go to. Rejects with Okta's message. */
  completeSignIn(): Promise<string>;
  signOut(): Promise<void>;
}

/** The part of `OktaAuth` the wrapper calls, so a test can pass a fake. */
export interface OktaLike {
  isAuthenticated(): Promise<boolean>;
  getOrRenewAccessToken(): Promise<string | null>;
  tokenManager: {
    getStorageKeyByType(type: 'accessToken'): string;
    renew(key: string): Promise<unknown>;
  };
  signInWithRedirect(options: { originalUri: string }): Promise<void>;
  handleLoginRedirect(): Promise<void>;
  signOut(options: { postLogoutRedirectUri: string }): Promise<boolean>;
}

export interface WrapOptions {
  /** The page Okta gave back to the `restoreOriginalUri` hook, if it did. */
  restoredUri: () => string | undefined;
  origin: string;
}

export function wrapOkta(okta: OktaLike, { restoredUri, origin }: WrapOptions): AuthClient {
  // StrictMode runs the callback's effect twice and an authorization code is good once.
  let completion: Promise<string> | undefined;
  return {
    isAuthenticated: () => okta.isAuthenticated(),
    accessToken: async () => {
      try {
        return (await okta.getOrRenewAccessToken()) ?? null;
      } catch {
        return null;
      }
    },
    renewAccessToken: async () => {
      try {
        const key = okta.tokenManager.getStorageKeyByType('accessToken');
        return accessTokenOf(await okta.tokenManager.renew(key));
      } catch {
        return null;
      }
    },
    signIn: (originalUri) => okta.signInWithRedirect({ originalUri }),
    completeSignIn: () => {
      completion ??= okta.handleLoginRedirect().then(() => localPath(restoredUri(), origin));
      return completion;
    },
    signOut: async () => {
      await okta.signOut({ postLogoutRedirectUri: `${origin}/` });
    },
  };
}

function accessTokenOf(token: unknown): string | null {
  if (typeof token !== 'object' || token === null || !('accessToken' in token)) return null;
  return typeof token.accessToken === 'string' ? token.accessToken : null;
}

/** The page to land on: on this origin only, and never the callback itself. */
function localPath(uri: string | undefined, origin: string): string {
  if (!uri) return '/';
  try {
    const url = new URL(uri, origin);
    if (url.origin !== origin) return '/';
    const path = `${url.pathname}${url.search}${url.hash}`;
    return url.pathname === CALLBACK_PATH ? '/' : path;
  } catch {
    return '/';
  }
}
